import React, { FC, useEffect, useState } from "react";
import { IRect } from "konva/lib/types";
import { 
    DEFAULT_LARGHEZZA_GUARNIZIONE,
    ALTEZZA_PANNELLO_PORTA_CLASSIC,
    SPESSORE_TELAIO_GLASSPANEL} from "../../../../../../../core/common_constants";
import { Circle, Group, Label, Line, Rect, Tag, Text } from "react-konva";
import { degree, toDrawUnit, VectorToPointsArray } from "../../../../../../../core/common_functions";
import { Vector } from "vecti";
import { useStoreSelector } from "../../../../../../../../hooks/StoreHooks";
import { FinishType, InstallationType, ModelType, NullableString, OpeningDirection } from "../../../../../../../core/common_types";
import { Option } from "../../../../../../../models/door_configuration";
import { DatiPannello } from "../../../../../../../core/common_draw_types";
import { useFinishes } from "../../../../../../../providers/finish_provider";
import { getStrokeColor, PlaceholderRect } from "../draw_utils";
import Konva from "konva";

const SPESSORE_TELAIO_EXT_PORTINA_LAT: number = 5;

export const PortinaLateraleVetrata: FC<{
    lpdraw: number, 
    hpdraw: number, 
    doorColor: HTMLImageElement|undefined,
    frameColor: HTMLImageElement|undefined,
    handleColor: HTMLImageElement|undefined}> = (props) => {
    const {lpdraw, hpdraw, doorColor, frameColor, handleColor} = props;
    const {doorConfigurationBuilder} = useStoreSelector(store => store);
    const strokeColor = getStrokeColor(doorConfigurationBuilder.getExternalColorCode());
    
    const buildPannelli = (): DatiPannello[] => {
        let spessoreTelaio = doorConfigurationBuilder.configuration?.portinaAffiancata?.tipoInstallazione == InstallationType.in_luce ? SPESSORE_TELAIO_EXT_PORTINA_LAT : 0;
        let altezzaAssoluta = hpdraw - spessoreTelaio;
        let ptTopLeft = Vector.of([0, altezzaAssoluta]);
        let ptTopRight = Vector.of([lpdraw - (2 * spessoreTelaio), altezzaAssoluta]);
        let larghezzaAssoluta = ptTopRight.x - ptTopLeft.x;
        
        let panels: DatiPannello[] = [];

        let p: DatiPannello = { 
            bbox: {x: ptTopLeft.x + spessoreTelaio, y: spessoreTelaio, width: larghezzaAssoluta, height: altezzaAssoluta},
            panelNumber: 1,
            panelCount: 1,
            larghezzaAssoluta,
            altezzaAssoluta: altezzaAssoluta,
            altezzaRelativa: larghezzaAssoluta,
            selectable: true,
        };

        panels.push(p);

        return panels;
    }
    
    const renderPanels = () => {
        let pannelli = buildPannelli();
        
        return pannelli.map((p, idx, ar) => (
            <PannelloPortaVetrata
                key={`panel_${(idx+1)}`}                
                panelData={p}
                doorColor={doorColor}
            />
        ));
    }

    const renderTelaio = () => {
        if (!(doorConfigurationBuilder.configuration?.portinaAffiancata?.portina || false)) return null;

        const altezzaPortina = toDrawUnit(doorConfigurationBuilder.configuration.portinaAffiancata.altezza);
        const larghezzaPortina = toDrawUnit(doorConfigurationBuilder.configuration.portinaAffiancata.larghezza);

        return <TelaioPortinaLaterale
            org={Vector.of([larghezzaPortina/2, altezzaPortina])}
            altezzaPortina={altezzaPortina}
            larghezzaPortina={larghezzaPortina}
            spessoreTelaio={SPESSORE_TELAIO_EXT_PORTINA_LAT}
            frameColor={frameColor}
            colorCode={doorConfigurationBuilder.getExternalColorCode()}
        />
    }

    const renderLabelCampione = () => {
        let pt1: Vector = Vector.of([0, hpdraw]);
        let pt2: Vector = Vector.of([lpdraw, 0]);
        let rads = degree(pt1.x, pt1.y, pt2.x, pt2.y);
        return (
            <Label x={(lpdraw/2)-75} y={(hpdraw/2)+40} rotation={rads} opacity={0.6}>
                <Tag fill="white" stroke="red"/>
                <Text text={"Colore da campione"} padding={5} fontSize={20} />
            </Label>
        );
    }

    const renderManiglia = () => {
        if (!(doorConfigurationBuilder.configuration?.portinaAffiancata?.portina || false)) return null;

        const altezzaPortina = toDrawUnit(doorConfigurationBuilder.configuration.portinaAffiancata.altezza);
        let larghezzaPortina = toDrawUnit(doorConfigurationBuilder.configuration.portinaAffiancata.larghezza);
        let sp = 0;
        
        if (doorConfigurationBuilder.configuration?.portinaAffiancata?.tipoInstallazione == InstallationType.in_luce) {
            larghezzaPortina -= 2 * SPESSORE_TELAIO_EXT_PORTINA_LAT;
            sp = SPESSORE_TELAIO_EXT_PORTINA_LAT;
        }
        
        return <Maniglia
            org={Vector.of([(larghezzaPortina/2)+sp, altezzaPortina])}
            altezzaPortina={altezzaPortina}
            larghezzaPortina={larghezzaPortina}
            pos={doorConfigurationBuilder.configuration?.portinaAffiancata?.versoApertura ?? "sx"}
            externalHandleColor={handleColor}
            colorCode={doorConfigurationBuilder.getExternalColorCode()}
        />
    }

    if (!doorConfigurationBuilder.configuration) return null;
    if (!doorConfigurationBuilder.configuration.porta) return null;
    if (!doorConfigurationBuilder.configuration.modello) return null;

    return (
        <Group>
            {doorConfigurationBuilder.configuration?.portinaAffiancata?.tipoInstallazione == InstallationType.in_luce && renderTelaio()}
            {renderPanels()}
            {renderManiglia()}
            
            {doorConfigurationBuilder.configuration.tipoColoreEsterno == "a_campione" && renderLabelCampione()}
        </Group>
    );
}

const PannelloPortaVetrata: FC<{doorColor: HTMLImageElement|undefined, panelData: DatiPannello, onClick?: (panelData: DatiPannello, placeholderData: PlaceholderRect) => void}> = (props) => {
    const {doorConfigurationBuilder} = useStoreSelector(store => store);
    const strokeColor = getStrokeColor(doorConfigurationBuilder.getExternalColorCode());

    const renderVetrata = () => {
        return (
            <Group x={props.panelData.bbox.x} y={props.panelData.bbox.y}>
                <Rect
                    x={SPESSORE_TELAIO_GLASSPANEL}
                    y={SPESSORE_TELAIO_GLASSPANEL} 
                    width={props.panelData.bbox.width- (2*SPESSORE_TELAIO_GLASSPANEL)} 
                    height={props.panelData.bbox.height - (2*SPESSORE_TELAIO_GLASSPANEL)} 
                    stroke={strokeColor} 
                    strokeWidth={0.75}
                    fill={"lightblue"}
                    // opacity={0.9}
                    // filters={[Konva.Filters.Blur]}
                    // blurRadius={20}
                />
            </Group>
        );
    }
    
    const getBboxDisegno = (panelData: DatiPannello): IRect => {       
        let org = Vector.of([panelData.bbox.x, panelData.bbox.y]);
        let larghezza = panelData.bbox.width;
        let altezza = panelData.bbox.height;

        if (doorConfigurationBuilder.isOltreLuce()) {  
            panelData.debugPt = [];
        }

        let rect: IRect =  {
            ...org,
            width: larghezza,
            height: altezza
        };

        panelData.org = {x: org.x, y: org.y};
        
        return rect;
    }

    return (
        <Group>
            <Rect            
                {...getBboxDisegno(props.panelData)}
                stroke={strokeColor} 
                strokeWidth={0.5}
                fillPatternImage={props.doorColor}
            />

            {renderVetrata()}
        </Group>
    );
}

const TelaioPortinaLaterale: FC<{ org: Vector, altezzaPortina: number, larghezzaPortina: number, spessoreTelaio: number, frameColor: HTMLImageElement|undefined, colorCode?: NullableString }> = (props) => {
    const { org, altezzaPortina, larghezzaPortina, spessoreTelaio, frameColor } = props;
    const strokeColor = getStrokeColor(props.colorCode);

    const getPointsTelaioSx = (): Vector[] => {
        let ptBottomLeft = Vector.of([org.x - (larghezzaPortina / 2), org.y]);
        let ptTopLeft = Vector.of([ptBottomLeft.x, org.y - altezzaPortina]);
        let ptTopRight = Vector.of([ptBottomLeft.x + spessoreTelaio, ptTopLeft.y + SPESSORE_TELAIO_EXT_PORTINA_LAT]);
        let ptBottomRight = Vector.of([ptTopRight.x, org.y]);

        return [
            ptBottomLeft,
            ptTopLeft,
            ptTopRight,
            ptBottomRight
        ];
    }

    const getPointsTelaioDx = (): Vector[] => {
        let ptTopRight = Vector.of([org.x + (larghezzaPortina / 2), org.y - altezzaPortina]);
        let ptBottomRight = Vector.of([ptTopRight.x, org.y]);
        let ptBottomLeft = Vector.of([ptBottomRight.x - spessoreTelaio, org.y]);
        let ptTopLeft = Vector.of([ptBottomLeft.x, ptTopRight.y + SPESSORE_TELAIO_EXT_PORTINA_LAT]);

        return [
            ptBottomLeft,
            ptTopLeft,
            ptTopRight,
            ptBottomRight
        ];
    }

    const getPointsTelaioSup = (): Vector[] => {
        let ptTopLeft = Vector.of([org.x - (larghezzaPortina / 2), org.y-altezzaPortina]);
        let ptTopRight = Vector.of([ptTopLeft.x + larghezzaPortina, ptTopLeft.y]);
        let ptBottomRight = Vector.of([ptTopRight.x - spessoreTelaio, ptTopLeft.y + spessoreTelaio]);
        let ptBottomLeft = Vector.of([ptTopLeft.x + spessoreTelaio, ptBottomRight.y]);

        return [
            ptBottomLeft,
            ptTopLeft,
            ptTopRight,
            ptBottomRight
        ];
    }

    return (
        <Group>
            <Line
                points={VectorToPointsArray(getPointsTelaioSx())}
                stroke={strokeColor}
                strokeWidth={0.5}
                fillPatternImage={frameColor}
                closed
            />
            <Line
                points={VectorToPointsArray(getPointsTelaioDx())}
                stroke={strokeColor}
                strokeWidth={0.5}
                fillPatternImage={frameColor}
                closed
            />
            <Line
                points={VectorToPointsArray(getPointsTelaioSup())}
                stroke={strokeColor}
                strokeWidth={0.5}
                fillPatternImage={frameColor}
                closed
            />
        </Group>
    );
}

const Maniglia: FC<{ org: Vector, larghezzaPortina: number, altezzaPortina: number, pos: OpeningDirection, externalHandleColor: HTMLImageElement|undefined, colorCode?: NullableString }> = (props) => {
    const { org, larghezzaPortina, altezzaPortina, pos, externalHandleColor } = props;
    const strokeColor = getStrokeColor(props.colorCode);

    const getPosManiglia = (): Vector => {
        if (pos == "dx") return Vector.of([
            org.x - (larghezzaPortina/2) + (SPESSORE_TELAIO_GLASSPANEL/2),
            org.y - (altezzaPortina/2)
        ])

        return Vector.of([
            org.x + (larghezzaPortina/2) - 12 - (SPESSORE_TELAIO_GLASSPANEL/2),
            org.y - (altezzaPortina/2)
        ]);
    }

    const getPosCopriManiglia = (): Vector => {
        if (pos == "dx") return Vector.of([
            org.x - (larghezzaPortina/2) + (SPESSORE_TELAIO_GLASSPANEL/2) - 1,
            org.y - (altezzaPortina/2) - 1.7
        ]);

        return Vector.of([
            org.x + (larghezzaPortina/2) - (SPESSORE_TELAIO_GLASSPANEL/2) - 1.25,
            org.y - (altezzaPortina/2) - 1.5
        ]);
    }

    const getPosCilindro = (): Vector => {
        if (pos == "dx") return Vector.of([
            org.x - (larghezzaPortina/2) + (SPESSORE_TELAIO_GLASSPANEL/2),
            org.y - (altezzaPortina/2) + 8
        ]);

        return Vector.of([
            org.x + (larghezzaPortina/2) - (SPESSORE_TELAIO_GLASSPANEL/2),
            org.y - (altezzaPortina/2) + 8
        ]);
    }

    return (
        <Group>
            <Rect
                {...getPosCopriManiglia()}
                width={2.5}
                height={5}
                fill={externalHandleColor == undefined ? "#000" : undefined}
                fillPatternImage={externalHandleColor != undefined ? externalHandleColor : undefined }
                cornerRadius={1}
                stroke={strokeColor}
                strokeWidth={0.25}
            />
            <Rect
                {...getPosManiglia()}
                width={12}
                height={2}
                fill={externalHandleColor == undefined ? "#000" : undefined}
                fillPatternImage={externalHandleColor != undefined ? externalHandleColor : undefined }
                cornerRadius={0.5}
                stroke={strokeColor}
                strokeWidth={0.25}
            />
            <Circle
                {...getPosCilindro()}
                radius={1.5}
                fill={externalHandleColor == undefined ? "#000" : undefined}
                fillPatternImage={externalHandleColor != undefined ? externalHandleColor : undefined }
                stroke={strokeColor}
                strokeWidth={0.25}
            />
            <Circle
                {...getPosCilindro()}
                radius={0.75}
                fill={externalHandleColor == undefined ? "#000" : undefined}
                fillPatternImage={externalHandleColor != undefined ? externalHandleColor : undefined }
                stroke={strokeColor}
                strokeWidth={0.25}
            />
        </Group>
    );
}